import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, 
  FileText, 
  PlusCircle, 
  Wallet, 
  Bot,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Vote,
  Shield,
  ShieldCheck,
  Building2,
  Package,
  Brain,
  Target,
  Zap,
  Flame,
  ScanText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/context/AuthContext';
import { useWallet } from '@/context/WalletContext';
import { KutroLogo } from '@/components/Logo';
import { NotificationDropdown } from '@/components/NotificationDropdown';
import { cn, truncateAddress } from '@/lib/utils';

export function DashboardLayout({ children, title }) {
  const { user, logout } = useAuth();
  const { 
    walletAddress, 
    isConnected, 
    connectWallet, 
    disconnectWallet,
    ethBalance,
    kutroBalance,
    isOnBase,
    switchToBase
  } = useWallet();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const isConsumer = user?.role === 'consumer';
  const isBusiness = user?.role === 'business';

  const consumerNav = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/consumer' },
    { icon: Bot, label: 'AI Agents', path: '/agents' },
    { icon: ScanText, label: 'Documents', path: '/consumer/documents' },
    { icon: Bot, label: 'AI Analysis', path: '/consumer/ai-analysis' },
    { icon: Wallet, label: 'Staking', path: '/staking' },
    { icon: Target, label: 'Predict', path: '/predict' },
    { icon: Vote, label: 'Governance', path: '/governance' },
    { icon: Zap, label: 'Velocity', path: '/velocity' },
    { icon: Flame, label: 'The Kojiru Quest', path: '/genesis-quest' },
    { icon: ShieldCheck, label: 'Verification', path: '/verification' },
    { icon: Settings, label: 'Settings', path: '/settings' },
  ];

  const lenderNav = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/lender' },
    { icon: FileText, label: 'Browse RFBs', path: '/lender/rfbs' },
    { icon: FileText, label: 'My Bids', path: '/lender/bids' },
    { icon: Wallet, label: 'Portfolio', path: '/lender/portfolio' },
    { icon: Shield, label: 'Protection', path: '/lender/protection' },
    { icon: Wallet, label: 'Staking', path: '/staking' },
    { icon: Target, label: 'Predict', path: '/predict' },
    { icon: Vote, label: 'Governance', path: '/governance' },
    { icon: Zap, label: 'Velocity', path: '/velocity' },
    { icon: Flame, label: 'The Kojiru Quest', path: '/genesis-quest' },
    { icon: ShieldCheck, label: 'Verification', path: '/verification' },
    { icon: Settings, label: 'Settings', path: '/settings' },
  ];

  const businessNav = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/business' },
    { icon: FileText, label: 'Receivables', path: '/business/receivables' },
    { icon: PlusCircle, label: 'New Loan', path: '/business/loan-request' },
    { icon: FileText, label: 'My Requests', path: '/business/requests' },
    { icon: Brain, label: 'AI Analysis', path: '/business/ai-analysis' },
    { icon: Wallet, label: 'Staking', path: '/staking' },
    { icon: Target, label: 'Predict', path: '/predict' },
    { icon: Vote, label: 'Governance', path: '/governance' },
    { icon: Zap, label: 'Velocity', path: '/velocity' },
    { icon: Flame, label: 'The Kojiru Quest', path: '/genesis-quest' },
    { icon: ShieldCheck, label: 'Verification', path: '/verification' },
    { icon: Settings, label: 'Settings', path: '/settings' },
  ];

  const navItems = isBusiness ? businessNav : isConsumer ? consumerNav : lenderNav;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed left-0 top-0 z-50 h-screen w-64 border-r border-white/5 bg-[#0A0A0A] transition-transform duration-300 lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-2 p-6 border-b border-white/5">
            <KutroLogo size={36} />
            <span className="text-xl font-bold bg-gradient-to-r from-orange-500 via-blue-500 to-purple-500 bg-clip-text text-transparent">KOJIRU</span>
            <button 
              className="ml-auto lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-xl transition-all",
                    isActive 
                      ? "bg-primary/10 text-primary border border-primary/20" 
                      : "text-muted-foreground hover:bg-white/5 hover:text-white"
                  )}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}

            {/* Sign Out */}
            <button
              onClick={handleLogout}
              data-testid="sidebar-sign-out-btn"
              className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all w-full text-red-400 hover:bg-red-500/10 hover:text-red-300 mt-2"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Sign Out</span>
            </button>
          </nav>

          {/* KOJIRU Balance */}
          <div className="p-4 border-t border-white/5">
            <div className="p-4 rounded-xl glass">
              <div className="text-sm text-muted-foreground mb-1">KOJIRU Balance</div>
              <div className="text-2xl font-bold gradient-text">
                {kutroBalance.toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground mt-1">≈ ${(kutroBalance * 0.10).toFixed(2)} USD</div>
            </div>
          </div>

          {/* User */}
          <div className="p-4 border-t border-white/5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-white/5 transition-colors">
                  <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center text-lg font-bold">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-medium truncate">{user?.name}</div>
                    <div className="text-xs text-muted-foreground capitalize">{user?.role}</div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="mr-2 w-4 h-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-500">
                  <LogOut className="mr-2 w-4 h-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 h-16 border-b border-white/5 bg-[#050505]/80 backdrop-blur-xl">
          <div className="flex items-center justify-between h-full px-6">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </button>
              <h1 className="text-xl font-semibold">{title}</h1>
            </div>

            <div className="flex items-center gap-4">
              {/* Network Warning */}
              {isConnected && !isOnBase && (
                <Button
                  data-testid="switch-network-btn"
                  variant="outline"
                  size="sm"
                  onClick={switchToBase}
                  className="border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10"
                >
                  Switch to Base
                </Button>
              )}

              {/* Wallet Connection */}
              {!isConnected ? (
                <Button
                  data-testid="connect-wallet-btn"
                  onClick={connectWallet}
                  className="gradient-primary"
                >
                  <Wallet className="mr-2 w-4 h-4" />
                  Connect Wallet
                </Button>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="border-white/10">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2" />
                      <span className="font-mono">{truncateAddress(walletAddress)}</span>
                      <ChevronDown className="ml-2 w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    <div className="p-3">
                      <div className="text-sm text-muted-foreground mb-1">Balance</div>
                      <div className="font-mono font-medium">{ethBalance} ETH</div>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={disconnectWallet}
                      className="text-red-500"
                    >
                      Disconnect
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Notifications */}
              <NotificationDropdown />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
}
